import { Component, Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfirmModalComponent, ConfirmModalData } from '../components/grid/modals/confirm-modal/confirm-modal.component';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ErrorModalComponent, ErrorModalData } from '../components/grid/modals/error-modal/error-modal.component';
import { ProgressModalComponent, ProgressModalData } from '../components/grid/modals/progress-modal/progress-modal.component';
import { SignaturePadModalComponent } from '../components/grid/modals/signature-pad-modal/signature-pad-modal.component';
import { CalculatorModalComponent, CalculatorModalData } from '../components/calculator-modal/calculator-modal.component';
import { TranslateService } from './translate.service';
import { FavouritesDialogComponent } from '@app/modal/favourites-dialog/favourites-dialog.component';
import { ReportPreviewDialogComponent, ReportPreviewDialogData } from '@app/modal/report-preview-dialog/report-preview-dialog.component';
import { StateDialogContext, StateModalComponent } from '@app/modal/state-modal/state-modal.component';
import { TranslateEditDialogComponent, TranslateEditDialogData } from '@app/modal/translate-edit-dialog/translate-edit-dialog.component';
import { DataTaskService } from './data-task.service';
import { StateModalPosition, StateModalSizeService } from '@app/modal/state-modal/state-modal-size.service';

export interface ModalConfig {
    type?: ModalType;

    title?: string;

    /**
     * @default false
     */
    translateTitle?: boolean;

    message?: string;

    /**
     * @default false
     */
    translateMessage?: boolean;

    buttons?: Array<{
        label: string;
        cssClass: string;
        close?: boolean;
        action?: (modalInstance: MatDialogRef<any>) => void;
        type?: 'link' | 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
    }>;

    state?: string;

    parms?: string;

    gototext?: string;

    /**
     * @default false
     */
    translateGototext?: boolean;
}

export type ModalType = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'none';

@Injectable({
    providedIn: 'root',
})
export class ModalService {
    constructor(
        private dialog: MatDialog,
        private datatask: DataTaskService,
        private stateModalSize: StateModalSizeService,
    ) {}

    /**
     * Opens a confirmation modal using the given strings to convey what to confirm.
     *
     * @param param configuration for the confirm modal.
     * @returns a promise returning `true` if the user confirmed, `false` or undefined if the user cancelled.
     */
    public async openConfirmDialog({ type = 'none', title, translateMessage, translateTitle, message, ok = 'ok', cancel = 'cancel' }: ConfirmModalData) {
        const dialogRef: MatDialogRef<ConfirmModalComponent, boolean> = this.dialog.open(ConfirmModalComponent, { data: { type: type, title: title, translateTitle: translateTitle, translateMessage: translateMessage, message: message, ok: ok, cancel: cancel }, minWidth: 'min(90vw, 35rem)' });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result === true) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Opens a warning dialog displaying a warning about expired session to the user. The user confirms and the session
     * is refreshed by reload the browser window.
     *
     * @param config
     * @returns a promise which resolves once the modal as been closed.
     */
    public async openSessionTimeoutWarningDialog(config?: ModalConfig) {
        const alreadyOpen = !!this.dialog.getDialogById('sessiontimeoutdialog');

        if (alreadyOpen) return;

        const isOnline = navigator.onLine;
        const dialogRef: MatDialogRef<MessageDialogComponent, undefined> = this.dialog.open(MessageDialogComponent, {
            id: 'sessiontimeoutdialog',
            data: <ModalConfig>{
                ...config,
                type: 'warning',
                title: 'timeout_warning_title',
                translateTitle: true,
                message: 'timeout_warning_message',
                translateMessage: true,
                buttons: [
                    {
                        label: 'timeout_warning_ok_label',
                        type: 'warning',
                        action: () => {
                            window.location.reload();
                            return false;
                        },
                        cssClass: '',
                    },
                ],
            },
            minWidth: '37rem',
        });

        return await firstValueFrom(dialogRef.afterClosed());
    }

    /**
     * Opens a error dialog displaying the given message. The message must be translated beforehand.
     *
     * @param message the error message to display in the error dialog.
     * @returns a promise which resolves once the modal as been closed.
     */
    public async openErrorDialog(message: string, config?: Partial<{ showCopyButton: boolean }>) {
        const dialogRef: MatDialogRef<ErrorModalComponent, undefined> = this.dialog.open(ErrorModalComponent, { data: <ErrorModalData>{ message: message, ...config }, minWidth: '37rem' });

        return await firstValueFrom(dialogRef.afterClosed());
    }

    /**
     * Open a progress dialog displaying the current progress
     * @param value
     * @param max
     * @param rememberId
     * @returns
     */

    public openProgressDialog(params: ProgressModalData, dialogOptions?: MatDialogConfig): ProgressModalComponent {
        const dialogRef: MatDialogRef<ProgressModalComponent> = this.dialog.open(ProgressModalComponent, { width: '60rem', ...dialogOptions, data: params });

        return dialogRef.componentInstance;
    }

    public async openStateDialog(params: { state: string; params?: string; width?: string; height?: string }, dialogConfig?: Partial<MatDialogConfig>) {
        const storedSize = await this.stateModalSize.getSize(params.state);
        const hasRememberedSize = storedSize?.useDefault !== true && storedSize?.width !== undefined && storedSize?.height !== undefined;
        const position: StateModalPosition | undefined = storedSize?.x !== undefined && storedSize?.y !== undefined ? { x: storedSize.x, y: storedSize.y } : undefined;
        const width = hasRememberedSize ? `${storedSize.width}px` : params.width;
        const height = hasRememberedSize ? `${storedSize.height}px` : params.height;

        const dialogRef: MatDialogRef<StateModalComponent> = this.dialog.open(StateModalComponent, {
            ...dialogConfig,
            data: {
                state: params.state,
                params: params.params,
                width: params.width,
                height: params.height,
                hasRememberedSize,
                closeOnNavigation: dialogConfig?.closeOnNavigation ?? true,
                position,
            },
            width,
            height,
            ...(position ? { position: { left: `${position.x}px`, top: `${position.y}px` } } : {}),
            minWidth: 'min(35rem, 100vw)',
            minHeight: 'min(20rem, 100vh)',
            maxWidth: '100vw',
            maxHeight: '100vh',
        });

        return await firstValueFrom(dialogRef.afterClosed());
    }

    /**
     * Opens a signaturePad Dialog
     * @returns a signature as SVG
     */
    public async openSignaturePadDialog(model?: string, dataformat?: string): Promise<string | undefined> {
        const dialogRef: MatDialogRef<SignaturePadModalComponent, string | undefined> = this.dialog.open(SignaturePadModalComponent, { data: { model: model ?? '', dataformat: dataformat ?? '' } });

        const result = firstValueFrom(dialogRef.afterClosed()); // Wait until the dialog is closed

        if (result) {
            return result; // This will be the signature data URL if it was returned
        } else {
            return undefined; // No signature was provided
        }
    }

    public show(config: ModalConfig, dialogConfig?: Partial<MatDialogConfig>): Promise<void> {
        const dialogRef = this.dialog.open(MessageDialogComponent, {
            width: '500px',
            ...dialogConfig,
            data: <ModalConfig>{
                ...config,
                type: config.type || 'secondary',
                title: config.title || ' ',
                translateTitle: config.translateTitle || false,
                message: config.message || '',
                translateMessage: config.translateMessage || '',
                buttons: config.buttons || [{ label: 'Ok', close: true, type: config.type }],
            },
        });

        return firstValueFrom(dialogRef.afterClosed());
    }

    public async openFavouritesDialog() {
        const dialogRef: MatDialogRef<FavouritesDialogComponent> = this.dialog.open(FavouritesDialogComponent, { width: '40rem' });

        return await firstValueFrom(dialogRef.afterClosed());
    }

    // BJS 20250306
    public async showCalculator(label?: string, plabel?: string, field?: any): Promise<any> {
        const dialogRef = this.dialog.open<CalculatorModalComponent, CalculatorModalData, any>(CalculatorModalComponent, {
            width: '400px',
            disableClose: true,
            data: { label, plabel, field },
        });

        // afterClosed() is an Observable => convert to Promise
        return lastValueFrom(dialogRef.afterClosed());
    }

    public async openReportPreviewDialog(data: ReportPreviewDialogData, modalConfig?: Partial<Omit<MatDialogConfig, 'data'>>) {
        const dialogRef = this.dialog.open(ReportPreviewDialogComponent, { ...modalConfig, data: data });

        return dialogRef;
    }

    public openTranslationDialog = async (params: { word_id: string; heading?: string; textToTranslate?: string; save?: (data: TranslateEditDialogData<any>) => Promise<unknown> }) => {
        if (!!params.word_id) {
            const edits = await this.datatask.Post(61, { word_id: params.word_id });
            const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
                width: '120rem',
                maxWidth: '95vw',
                height: '80vh',
                data: <TranslateEditDialogData<any>>{
                    heading: params?.heading ?? `Translate edit - ${params.word_id}`,
                    textToTranslate: params.textToTranslate ?? params.word_id,
                    edits: edits,
                    save: async (data) => {
                        if (!!params.save) {
                            await params.save(data);
                        } else {
                            const response = await this.datatask.Post(62, { wordlang_names: data.edits });
                            dialogRef.close(response);
                        }
                    },
                },
            });

            return firstValueFrom(dialogRef.beforeClosed());
        }

        throw Error('no word id');
    };
}

@Component({
    selector: 'app-message-dialog',
    template: `
        <h2 mat-dialog-title cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragBoundary=".cdk-overlay-container" cdkDragHandle [ngClass]="!!model.type && model.type !== 'none' ? 'tt-' + model.type + '-background' : ''" [style.fontSize]="'var(--tt-font-size-xl)'">
            {{ model.title }}
        </h2>
        <mat-dialog-content>
            <p [style.fontSize]="'calc(var(--tt-font-size))'" style="padding: 40px 50px; padding-bottom: 20px; color: var(--tt-body-text-color); white-space: pre-wrap">
                <span>{{ model.message }}</span>
                <ng-container *ngIf="!!model.gototext && !!model.state">
                    <br />
                    <a>{{ model.gototext }}</a>
                </ng-container>
            </p>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <tt-button *ngFor="let button of model.buttons; index as i" class="col-xs-3 sp-2" [ttText]="button?.label || ''" [ttType]="button?.type ?? 'secondary'" (ttClick)="buttonClicked(i)"></tt-button>
            <!-- <tt-button class="col-xs-3 sp-2" ttText="ok" [ttType]="!data.type || data.type === 'none' ? 'primary' : data.type" (ttClick)="dialogRef.close(true)" [ttStyle]="style"></tt-button> -->
        </mat-dialog-actions>
    `,
})
export class MessageDialogComponent {
    model: ModalConfig;

    constructor(
        public dialogRef: MatDialogRef<MessageDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ModalConfig,
        private translate: TranslateService,
    ) {
        this.model = data || ({} as ModalConfig);
        console.log(this.model);
        this.setTitle();
        this.setMessage();
        this.setGototext();
    }

    private async setTitle() {
        if (this.model.translateTitle === true && this.model.title) {
            this.model.title = await this.translate.translate(this.model.title);
        }
    }

    private async setMessage() {
        if (this.model.translateMessage === true && this.model.message) {
            this.model.message = await this.translate.translate(this.model.message);
        }
    }

    private async setGototext() {
        if (this.model.translateGototext === true && this.model.gototext) {
            this.model.gototext = await this.translate.translate(this.model.gototext);
        }
    }

    buttonClicked(index: number): void {
        if (!this.model) return;
        if (!this.model.buttons) return;
        if (this.model.buttons.length < index + 1) return;

        const button = this.model.buttons[index];

        if (button.close) {
            this.dialogRef.close();
        } else if (button.action) {
            button.action(this.dialogRef);
        }
    }

    close(): void {
        this.dialogRef.close();
    }
}
